import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { ShieldCheck, KeyRound, Loader2, Copy, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError, apiErrorCode } from '../lib/apiError';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter-46ah — Two-factor authentication flow.
 *
 * Renders one of three sub-states based on what /auth/login returned:
 *
 *   mode="verify"  → user has MFA enrolled. Show 6-digit input + "use
 *                    backup code" toggle. POST /auth/mfa/challenge.
 *   mode="enroll"  → grace counter expired. Show QR + first-code input
 *                    + backup-code reveal. POST /auth/mfa/enroll-setup
 *                    on mount; then /auth/mfa/enroll-verify on submit.
 *
 * Props:
 *   mode           "verify" | "enroll"
 *   mfaToken       short-lived bearer for the bridge endpoints
 *   onComplete(d)  called with the final login response payload (cookies
 *                  are already set by the server before we get here)
 *   onCancel()     return to the password form
 */
export const MFAChallengeFlow = ({ mode, mfaToken, onComplete, onCancel }) => {
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enrollData, setEnrollData] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [acknowledgedBackup, setAcknowledgedBackup] = useState(false);

  // Kick off the enroll-setup as soon as the component mounts in enroll mode.
  React.useEffect(() => {
    if (mode !== 'enroll' || enrollData) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/auth/mfa/enroll-setup`,
          {},
          { headers: { Authorization: `Bearer ${mfaToken}` } }
        );
        if (!cancelled) setEnrollData(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to start MFA setup'));
      }
    })();
    return () => { cancelled = true; };
  }, [mode, mfaToken, enrollData]);

  const handleSubmitVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/mfa/challenge`,
        { code, is_backup_code: useBackup },
        { headers: { Authorization: `Bearer ${mfaToken}` }, withCredentials: true }
      );
      onComplete(data);
    } catch (err) {
      // Iter-49: surface `mfa_reset_required` (encryption-key rotated after
      // enrollment) with a persistent toast + recovery hint instead of a
      // vanishing "Invalid 6-digit code" that misleads the user.
      if (apiErrorCode(err) === 'mfa_reset_required') {
        toast.error(formatApiError(err, 'MFA reset required'), {
          duration: 15000,
          description: 'Try a backup code if you have one, otherwise ask an administrator to reset your MFA.',
        });
      } else {
        toast.error(formatApiError(err, useBackup ? 'Invalid backup code' : 'Invalid 6-digit code'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEnroll = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/mfa/enroll-verify`,
        { code },
        { headers: { Authorization: `Bearer ${mfaToken}` }, withCredentials: true }
      );
      setBackupCodes(data.backup_codes);
      // Hold onto the login payload — we'll fire onComplete after the user
      // confirms they've saved the backup codes.
      window.__mfaPendingPayload = data;
    } catch (err) {
      toast.error(formatApiError(err, 'Invalid code — please try again'));
    } finally {
      setLoading(false);
    }
  };

  const finalizeEnrollment = () => {
    const data = window.__mfaPendingPayload;
    delete window.__mfaPendingPayload;
    onComplete(data);
  };

  const downloadCodes = () => {
    const txt = `StudioScheduler — MFA Backup Codes\nGenerated ${new Date().toISOString()}\n\nEach code can be used ONCE. Keep them somewhere safe.\n\n${backupCodes.join('\n')}\n`;
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'studioscheduler-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  };

  // ─── Backup code display after successful enrollment ─────────────────────
  if (backupCodes) {
    return (
      <div className="space-y-4" data-testid="mfa-backup-codes-screen">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
          <h3 className="text-lg font-semibold">MFA Enabled!</h3>
          <p className="text-sm text-muted-foreground">
            Save these backup codes — each one works once if you lose your phone.
          </p>
        </div>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Save these now</AlertTitle>
          <AlertDescription>
            You will <strong>never</strong> see these codes again. Store them in a password manager or print them.
          </AlertDescription>
        </Alert>
        <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm" data-testid="mfa-backup-codes-list">
          {backupCodes.map((c) => (
            <div key={c} className="text-center select-all">{c}</div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={copyCodes} data-testid="mfa-copy-codes-btn">
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
          <Button type="button" variant="outline" className="flex-1" onClick={downloadCodes} data-testid="mfa-download-codes-btn">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        </div>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledgedBackup}
            onChange={(e) => setAcknowledgedBackup(e.target.checked)}
            className="mt-1"
            data-testid="mfa-backup-ack-checkbox"
          />
          <span>I've saved my backup codes somewhere safe</span>
        </label>
        <Button
          type="button"
          className="w-full"
          disabled={!acknowledgedBackup}
          onClick={finalizeEnrollment}
          data-testid="mfa-backup-continue-btn"
        >
          Continue
        </Button>
      </div>
    );
  }

  // ─── Enrollment QR + first code ──────────────────────────────────────────
  if (mode === 'enroll') {
    if (!enrollData) {
      return (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">Preparing MFA setup…</p>
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmitEnroll} className="space-y-4" data-testid="mfa-enroll-screen">
        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Set up two-factor authentication</AlertTitle>
          <AlertDescription>
            Your account requires MFA. Scan the QR code with Google Authenticator, Microsoft Authenticator, or Authy — then enter the 6-digit code below.
          </AlertDescription>
        </Alert>
        <div className="flex justify-center bg-white p-4 rounded-lg border" data-testid="mfa-qr-container">
          <QRCodeSVG value={enrollData.provisioning_uri} size={180} level="M" />
        </div>
        <div className="text-xs text-muted-foreground text-center">
          Can't scan? Enter this secret manually in your app:
          <div className="font-mono mt-1 select-all break-all" data-testid="mfa-secret">{enrollData.secret}</div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="enroll-code">6-digit code from your app</Label>
          <Input
            id="enroll-code"
            inputMode="numeric"
            maxLength={6}
            pattern="\d{6}"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            autoFocus
            required
            data-testid="mfa-enroll-code-input"
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading || code.length !== 6} data-testid="mfa-enroll-verify-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable MFA'}
        </Button>
      </form>
    );
  }

  // ─── Verify (login challenge) ─────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmitVerify} className="space-y-4" data-testid="mfa-verify-screen">
      <div className="text-center">
        <KeyRound className="w-10 h-10 text-primary mx-auto mb-2" />
        <h3 className="text-lg font-semibold">Two-factor authentication</h3>
        <p className="text-sm text-muted-foreground">
          {useBackup
            ? 'Enter one of your backup codes (XXXX-XXXX)'
            : 'Enter the 6-digit code from your authenticator app'}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="mfa-code">{useBackup ? 'Backup code' : 'Code'}</Label>
        <Input
          id="mfa-code"
          inputMode={useBackup ? 'text' : 'numeric'}
          maxLength={useBackup ? 9 : 6}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={useBackup ? 'XXXX-XXXX' : '000000'}
          autoFocus
          required
          data-testid="mfa-challenge-code-input"
        />
      </div>
      <Button
        type="submit"
        className="w-full"
        disabled={loading || code.length < (useBackup ? 9 : 6)}
        data-testid="mfa-challenge-submit-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
      </Button>
      <div className="flex justify-between text-sm">
        <button
          type="button"
          onClick={() => { setUseBackup((v) => !v); setCode(''); }}
          className="text-primary hover:underline"
          data-testid="mfa-toggle-backup-btn"
        >
          {useBackup ? '← Use authenticator app' : 'Use a backup code instead'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted-foreground hover:underline"
          data-testid="mfa-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
